'use client';

import { useEffect, useState } from 'react';
import { listDocumentation, type HelpDocument } from '@/lib/help/api';
import { featureCategories, useCaseCategories, type DocCategory } from '@/lib/documentation/categories';
import { DocumentationCategoryCard } from './DocumentationCategoryCard';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface CategoryWithCount extends DocCategory {
  docCount: number;
}

export function DocumentationHub() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featureCategoriesWithCounts, setFeatureCategoriesWithCounts] = useState<CategoryWithCount[]>([]);
  const [useCaseCategoriesWithCounts, setUseCaseCategoriesWithCounts] = useState<CategoryWithCount[]>([]);

  useEffect(() => {
    async function fetchDocs() {
      try {
        setLoading(true);
        setError(null);
        const docs = await listDocumentation();

        // Count docs for each feature category
        const featureWithCounts = featureCategories.map((category) => {
          const count = docs.filter((doc) => {
            const docPath = doc.route.replace(/^\//, '');
            return docPath.startsWith(category.basePath + '/') || docPath === category.basePath;
          }).length;
          return { ...category, docCount: count };
        });

        // Count docs for each use case category
        const useCaseWithCounts = useCaseCategories.map((category) => {
          const count = docs.filter((doc) => {
            const docPath = doc.route.replace(/^\//, '');
            return docPath.startsWith(category.basePath + '/') || docPath === category.basePath;
          }).length;
          return { ...category, docCount: count };
        });

        setFeatureCategoriesWithCounts(featureWithCounts);
        setUseCaseCategoriesWithCounts(useCaseWithCounts);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load documentation');
      } finally {
        setLoading(false);
      }
    }

    fetchDocs();
  }, []);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-8">
      {/* By Feature Area */}
      <section>
        <h2 className="text-lg font-semibold mb-4">By Feature Area</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-32 rounded-xl" />
              ))
            : featureCategoriesWithCounts.map((category) => (
                <DocumentationCategoryCard
                  key={category.id}
                  id={category.id}
                  title={category.title}
                  description={category.description}
                  icon={category.icon}
                  docCount={category.docCount}
                  href={`/docs/${category.basePath}`}
                />
              ))}
        </div>
      </section>

      {/* By Use Case */}
      <section>
        <h2 className="text-lg font-semibold mb-4">By Use Case</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-32 rounded-xl" />
              ))
            : useCaseCategoriesWithCounts.map((category) => (
                <DocumentationCategoryCard
                  key={category.id}
                  id={category.id}
                  title={category.title}
                  description={category.description}
                  icon={category.icon}
                  docCount={category.docCount}
                  href={`/docs/${category.basePath}`}
                />
              ))}
        </div>
      </section>
    </div>
  );
}
